import React, { useState, useEffect, type ReactNode } from "react";
import { RouteComponentProps } from "react-router";
import { useUser } from "../state";
import { API_EventId } from "../../../src/apidefs";
import { api } from "../api";
import { DateTimeInput } from "../DesignSystem/DateTimeInput";
import { CalendarDaysIcon } from "@heroicons/react/24/outline";
import { TestIds } from "../testIds";

const Card = ({
  children,
  className = "",
}: {
  children: ReactNode;
  className?: string;
}) => (
  <div
    className={`rounded-2xl border border-slate-200 bg-white p-6 dark:border-white/10 dark:bg-slate-800 ${className}`}
  >
    {children}
  </div>
);

const CardTitle = ({
  children,
  icon,
}: {
  children: ReactNode;
  icon?: ReactNode;
}) => (
  <h3 className="mb-4 flex items-center gap-2 text-lg font-semibold text-slate-800 dark:text-white">
    {icon && <span className="text-slate-500 dark:text-slate-400">{icon}</span>}
    {children}
  </h3>
);

const FormField = ({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) => (
  <div className="space-y-1.5">
    <label className="block text-sm font-medium text-slate-600 dark:text-slate-300">
      {label}
    </label>
    {children}
  </div>
);

const inputClassName =
  "w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-700 dark:text-white";

export function EditEventView({
  match,
}: RouteComponentProps<{ id: string }>): JSX.Element {
  const user = useUser();
  const [event, setEvent] = useState<API_EventId | undefined>(undefined);

  useEffect(() => {
    const fetchEvent = async () => {
      const result = await api.get<"/api/event/:id">(
        `/api/event/${match.params.id}`
      );
      setEvent(result);
    };
    fetchEvent();
  }, [match.params.id]);

  if (user.type !== "logged-in" || !user.user.admin || !event) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-100 dark:bg-slate-900">
        <div className="text-slate-500 dark:text-slate-400">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 px-4 pb-8 pt-16 dark:bg-slate-900">
      <div className="mx-auto max-w-2xl space-y-6">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-800 dark:text-white">
            Edit Event
          </h1>
          <p className="mt-1 text-slate-500 dark:text-slate-400">
            Update event details
          </p>
        </div>

        <Card>
          <CardTitle icon={<CalendarDaysIcon className="h-5 w-5" />}>
            Event Details
          </CardTitle>
          <form
            action={`/api/event/${match.params.id}/edit`}
            method="post"
            className="space-y-4"
          >
            <FormField label="Event Title">
              <input
                type="text"
                name="title"
                defaultValue={event.title}
                className={inputClassName}
                data-testid={TestIds.EditEventTitleInput}
              />
            </FormField>

            <FormField label="Description">
              <textarea
                name="description"
                defaultValue={event.description}
                placeholder="What's this event about?"
                rows={3}
                className={inputClassName}
              />
            </FormField>

            <FormField label="Event URL">
              <input
                type="text"
                name="url"
                defaultValue={event.url}
                placeholder="probably a discord link"
                className={inputClassName}
              />
            </FormField>

            <FormField label="Start Time">
              <DateTimeInput
                name="start"
                value={String(event.start)}
                inputClassName={inputClassName}
                dataTestId={TestIds.EditEventStartInput}
              />
            </FormField>

            <FormField label="Image URL">
              <input
                type="text"
                name="image"
                defaultValue={event.image}
                placeholder="https://example.com/image.png"
                className={inputClassName}
              />
            </FormField>

            <div className="pt-2">
              <button
                type="submit"
                className="rounded-lg bg-blue-600 px-6 py-2.5 font-medium text-white transition hover:bg-blue-700"
                data-testid={TestIds.EditEventSubmitButton}
              >
                Save Changes
              </button>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
}
